import { themeVar as colors } from "../design-tokens";

/**
 * WHAT CHANGED SINCE YOU CONFIRMED THE ADDRESS (2026-08-05).
 *
 * The owner's complaint, verbatim: "the tool makes us confirm the subject file multiple times lets
 * make sure this is intuitive for the agent to walk through."
 *
 * The measured path: step 1 looks the address up and renders the 24-row subject form. The agent
 * ticks and corrects about twenty rows. They upload the comps export, wait out an OCR pass, and
 * land on step 2 — which rendered THE SAME 24 rows, over a second piece of state, under a fresh
 * "correct anything that's wrong" instruction. Nothing on that screen said which of those rows they
 * had already dealt with, so the honest reading of it was "do all of that again".
 *
 * This is the sentence that was missing. It is a summary and nothing else: it holds no control, it
 * changes no value, and it makes no claim the rows below do not also make. What it does is tell the
 * agent, in three short lines, that most of this screen is settled and exactly which part is not.
 *
 * Deliberately NOT a "confirm these changes" button. There is nothing to accept — a merged field is
 * already ticked and already source-tagged, and the rows themselves are where a field is accepted
 * or rejected, one at a time, the way every other fact in this product is.
 */
export function SubjectCarryOver({
  carriedCount,
  addedLabels,
  missingLabels,
  backLabel,
}: {
  /** How many details the agent confirmed on step 1 and are being carried, not re-asked. */
  carriedCount: number;
  /** The details a SOURCE has added since step 1 — the uploaded file, or a public-records fetch.
   * Each row below says which; this is the count and the names.
   *
   * NOT WHAT THE AGENT TYPED, and not a blank (2026-08-06, owner defect). This list used to be
   * every field confirmed now and not confirmed on step 1, which includes a value the agent had
   * just typed into the screen they are looking at — announced back to them as something that "has
   * landed since", under a sentence telling them each row says where it came from. `CmaAmbFlow`
   * filters on the source tag now, so this really is the file-vs-record diff it claims to be. */
  addedLabels: string[];
  /** Required details still not confirmed. The readiness gate at the foot of this step is the
   * authority on these and blocks the build on them; this is the same fact, said at the top, where
   * the agent's eye starts. */
  missingLabels: string[];
  /* NO `notice` PROP (removed 2026-08-05, review finding). It used to carry the "your sheet is a
   * record for a different property" disclosure, which meant that disclosure rendered only where
   * THIS component renders — inside `{step1Grid && …}` — and was silent everywhere else. A summary
   * of what changed may be conditional. A disclosure may not. `CmaAmbFlow` renders it in a block of
   * its own on step 2, unconditionally, and it is deliberately not accepted back here. */
  /** What the Back control on this screen is called, so the text can point at it by name instead
   * of describing a button the agent then has to go and find. */
  backLabel: string;
}) {
  const nothingNew = addedLabels.length === 0;
  return (
    <section
      aria-label="What changed since you confirmed the address"
      style={{
        marginTop: 16,
        padding: "14px 16px",
        borderRadius: 10,
        background: colors.bgSurface,
        border: `1px solid ${colors.border}`,
      }}
    >
      {carriedCount > 0 && (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: colors.textPrimary }}>
          <strong>
            {carriedCount} detail{carriedCount === 1 ? "" : "s"} you confirmed on the last screen{" "}
            {carriedCount === 1 ? "is" : "are"} carried over.
          </strong>{" "}
          <span style={{ color: colors.textSecondary }}>
            They are listed further down, ticked and source-tagged, and you are not being asked
            about them again. Correct any of them there if you need to.
          </span>
        </p>
      )}

      <p style={{ margin: carriedCount > 0 ? "8px 0 0" : 0, fontSize: 13, lineHeight: 1.55, color: colors.textPrimary }}>
        {nothingNew ? (
          <span style={{ color: colors.textSecondary }}>
            Your file added nothing to the subject&apos;s own details — everything it holds about
            this home, you already had. Its sales are on the next step.
          </span>
        ) : (
          <>
            <strong>
              Your file and the public record added {addedLabels.length}{" "}
              {addedLabels.length === 1 ? "detail" : "details"}:
            </strong>{" "}
            <span style={{ color: colors.textSecondary }}>
              {addedLabels.join(", ")}. Each row says where it came from. Untick anything you
              don&apos;t want used.
            </span>
          </>
        )}
      </p>

      {missingLabels.length > 0 && (
        <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.55, color: colors.caution }}>
          <strong>
            Still to confirm: {missingLabels.join(", ")}.
          </strong>{" "}
          <span style={{ color: colors.textSecondary }}>
            The report cannot compute without {missingLabels.length === 1 ? "it" : "them"}. Nothing
            below is guessed to fill the gap.
          </span>
        </p>
      )}

      <p style={{ margin: "10px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
        The address itself is set on the previous screen — use &ldquo;{backLabel}&rdquo; above to
        change it. Everything you have entered here is kept.
      </p>
    </section>
  );
}
